import React, { useState, createContext, useContext } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useI18n } from '../i18n/index.jsx';

const SidebarContext = createContext(undefined);

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider");
  }
  return context;
};

export const SidebarProvider = ({
  children,
  open: openProp,
  setOpen: setOpenProp,
  animate = true,
}) => {
  const [openState, setOpenState] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const open = openProp !== undefined ? openProp : openState;
  const setOpen = setOpenProp !== undefined ? setOpenProp : setOpenState;

  return (
    <SidebarContext.Provider value={{ open, setOpen, animate, mobileMenuOpen, setMobileMenuOpen }}>
      {children}
    </SidebarContext.Provider>
  );
};

export const Sidebar = ({
  children,
  open,
  setOpen,
  animate,
}) => {
  return (
    <SidebarProvider open={open} setOpen={setOpen} animate={animate}>
      {children}
    </SidebarProvider>
  );
};

export const SidebarBody = (props) => {
  return (
    <>
      <DesktopSidebar {...props} />
      <MobileSidebar {...props} />
    </>
  );
};

export const DesktopSidebar = ({
  className,
  children,
  ...props
}) => {
  const { t } = useI18n();
  const { open, setOpen, animate } = useSidebar();
  const [isDesktop, setIsDesktop] = React.useState(false);
  
  React.useEffect(() => {
    const checkDesktop = () => {
      setIsDesktop(window.innerWidth >= 768);
    };
    checkDesktop();
    window.addEventListener('resize', checkDesktop);
    return () => window.removeEventListener('resize', checkDesktop);
  }, []);
  
  if (!isDesktop) return null;
  
  return (
    <motion.div
      className={`h-screen px-4 py-4 flex flex-col justify-between bg-slate-900/50 backdrop-blur-xl border-r border-slate-700/50 flex-shrink-0 fixed left-0 top-0 z-50 ${className || ''}`}
      animate={{
        width: animate ? (open ? "280px" : "90px") : "280px",
      }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      {...props}
    >
      {children}
    </motion.div>
  );
};

export const MobileSidebar = ({
  className,
  children,
  ...props
}) => {
  const { t } = useI18n();
  const { mobileMenuOpen, setMobileMenuOpen } = useSidebar();
  const [isMobile, setIsMobile] = React.useState(false);
  
  React.useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      // Fermer le menu si on passe en desktop
      if (!mobile && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [mobileMenuOpen, setMobileMenuOpen]);
  
  if (!isMobile) return null;
  
  return (
    <>
      <div
        className="h-14 px-3 py-2 flex flex-row items-center justify-between bg-slate-900/95 backdrop-blur-xl border-b border-slate-700/50 w-full fixed top-0 left-0 right-0 z-[100] shadow-lg"
        style={{ display: isMobile ? 'flex' : 'none' }}
        {...props}
      >
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 flex items-center justify-center">
            <img src="/logo_svg.svg" alt="Hiperyon" className="h-9 w-9" />
          </div>
          <span className="font-bold text-lg text-white">Hiperyon</span>
        </div>
        <button 
          className="flex items-center justify-center p-2 hover:bg-slate-800 rounded-lg transition-colors active:scale-95 touch-manipulation"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Menu"
        >
          <Menu className="text-white h-6 w-6" />
        </button>
      </div>
      
      <AnimatePresence>
        {mobileMenuOpen && isMobile && (
          <div className="md:hidden">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[110]"
              onClick={() => setMobileMenuOpen(false)}
            />
            <motion.div
              initial={{ x: "-100%", opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: "-100%", opacity: 0 }}
              transition={{
                duration: 0.3,
                ease: "easeInOut",
              }}
              className={`fixed h-full w-72 max-w-[80vw] left-0 top-0 bg-slate-900 backdrop-blur-2xl border-r border-emerald-500/30 p-5 z-[120] flex flex-col shadow-2xl ${className || ''}`}
            >
              <div
                className="absolute right-3 top-3 z-50 text-slate-200 cursor-pointer hover:bg-slate-800 rounded-lg p-2 transition-colors touch-manipulation"
                onClick={() => setMobileMenuOpen(false)}
              >
                <X className="h-5 w-5" />
              </div>
              <div className="space-y-2 mt-10 overflow-y-auto flex-1 pb-4">
  {children}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

export const SidebarLink = ({
  link,
  className,
  ...props
}) => {
  const { open, animate, setMobileMenuOpen } = useSidebar();
  const location = useLocation();
  const isActive = location.pathname === link.href;
  const [isMobile, setIsMobile] = React.useState(false);
  
  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  const handleClick = (e) => {
    // Fermer le menu mobile uniquement après un clic
    if (window.innerWidth < 768) {
      setMobileMenuOpen(false);
    }
  };
  
  return (
    <Link
      to={link.href}
      onClick={handleClick}
      className={`flex items-center justify-start gap-3 group/sidebar py-3 px-3 rounded-xl transition-all duration-200 relative ${
        isActive 
          ? 'bg-gradient-to-r from-emerald-500/20 to-emerald-600/20 text-emerald-300 shadow-lg shadow-emerald-500/10 border border-emerald-500/30' 
          : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-200 hover:shadow-md'
      } ${className || ''}`}
      {...props}
    >
      <div className="flex-shrink-0 relative">
        {link.icon}
  {/* active background effect removed for a cleaner look */}
      </div>
      <motion.span
        animate={{
          display: isMobile ? "inline-block" : (animate ? (open ? "inline-block" : "none") : "inline-block"),
          opacity: isMobile ? 1 : (animate ? (open ? 1 : 0) : 1),
        }}
        className="text-sm font-medium group-hover/sidebar:translate-x-1 transition duration-200 whitespace-pre inline-block !p-0 !m-0"
      >
        {link.label}
      </motion.span>
  {/* left active indicator removed */}
    </Link>
  );
};
